import { useCallback, useEffect, useRef, useState } from "react";

interface PageParams {
  offset: number;
  limit: number;
}

interface PageResult<T> {
  items: T[];
  count: number;
}

interface UseInfinitePaginationParams<T> {
  fetchPage: (params: PageParams) => Promise<PageResult<T>>;
  onItemsChange?: (items: T[]) => void;
  onError?: (error: unknown) => void;
  pageSize?: number;
  rootMargin?: string;
}

const useInfinitePagination = <T>({
  fetchPage,
  onItemsChange,
  onError,
  pageSize = 20,
  rootMargin = "200px",
}: UseInfinitePaginationParams<T>) => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<T[]>([]);
  const requestInFlightRef = useRef(false);
  const fetchPageRef = useRef(fetchPage);
  const onItemsChangeRef = useRef(onItemsChange);
  const onErrorRef = useRef(onError);
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  fetchPageRef.current = fetchPage;
  onItemsChangeRef.current = onItemsChange;
  onErrorRef.current = onError;

  const loadPage = useCallback(async (reset = false) => {
    if (requestInFlightRef.current) {
      return;
    }

    requestInFlightRef.current = true;
    setIsLoading(true);

    try {
      const offset = reset ? 0 : itemsRef.current.length;
      const response = await fetchPageRef.current({ offset, limit: pageSize });
      const nextItems = reset
        ? response.items
        : [...itemsRef.current, ...response.items];

      itemsRef.current = nextItems;
      setItems(nextItems);
      setTotalCount(response.count);
      onItemsChangeRef.current?.(nextItems);
    } catch (error) {
      onErrorRef.current?.(error);
    } finally {
      requestInFlightRef.current = false;
      setIsLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    loadPage(true);
  }, [loadPage]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target || isLoading || items.length >= totalCount) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadPage();
      }
    }, { rootMargin });

    observer.observe(target);

    return () => observer.disconnect();
  }, [isLoading, items.length, loadPage, rootMargin, totalCount]);

  return {
    items,
    totalCount,
    isLoading,
    hasMore: items.length < totalCount,
    loadMoreRef,
    loadMore: () => loadPage(),
    refresh: () => loadPage(true),
  };
};

export default useInfinitePagination;
